// ローカルストレージ管理ユーティリティ

import logger from '../utils/logger';
import {
  ExtendedCustomer,
  ExtendedReservation,
  generateCustomerCode,
} from '../types/salonboard';
import {
  StaffShiftData,
  ExtendedStaffShiftData,
} from '../types/extended-shift';
import CryptoJS from 'crypto-js';

const STORAGE_KEYS = {
  CUSTOMERS: 'ripia_customers',
  RESERVATIONS: 'ripia_reservations',
  TREATMENTS: 'ripia_treatments',
  MEDICAL_RECORDS: 'ripia_medical_records',
  SEQUENCES: 'ripia_sequences',
  STAFF_SHIFTS: 'ripia_staff_shifts',
  STAFF_SHIFTS_EXTENDED: 'ripia_extended_staff_shifts',
} as const;

// 暗号化キー（環境変数から取得、フォールバックあり）
const ENCRYPTION_KEY =
  import.meta.env.VITE_ENCRYPTION_KEY || 'Ripia-PII-Encryption-Key-2025-Dev';

// 暗号化関数
function encrypt<T>(data: T): string {
  const jsonString = JSON.stringify(data);
  return CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
}

// 復号化関数
function decrypt<T>(encryptedData: string): T | null {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    logger.error('Decryption failed:', error);
    return null;
  }
}

// サロンボード互換機能を含む拡張Customer型を使用
export type Customer = ExtendedCustomer;

// 正式なTreatmentRecord型を再エクスポート（types/treatment.tsから）
export type { TreatmentRecord } from '../types/treatment';

// テストモードフラグの定義（関数として定義して実行時に評価）
const isTestMode = () => {
  return (
    import.meta.env.MODE === 'test' ||
    (typeof window !== 'undefined' &&
      window.location?.search?.includes('test=true'))
  );
};

// 顧客データの取得（暗号化対応 + テストモード対応）
export function getCustomers(): Customer[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CUSTOMERS);
    if (!stored) return [];

    // テストモードでは暗号化をスキップ
    if (isTestMode()) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        logger.error('Test mode: Failed to parse customers data:', error);
        return [];
      }
    }

    // 本番モードでは暗号化処理
    try {
      // まず復号化を試みる
      const decrypted = decrypt<Customer[]>(stored);
      if (decrypted) return decrypted;
    } catch {
      // 復号化に失敗した場合は、平文として扱う（移行期間の対応）
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }

    return [];
  } catch (error) {
    logger.error('Failed to load customers from localStorage:', error);
    return [];
  }
}

// 顧客データの保存（暗号化対応）
export function saveCustomers(customers: Customer[]): void {
  try {
    // テストモードでは暗号化をスキップ
    if (isTestMode()) {
      localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify(customers));
    } else {
      // 顧客データを暗号化して保存
      const encrypted = encrypt(customers);
      localStorage.setItem(STORAGE_KEYS.CUSTOMERS, encrypted);
    }
  } catch (error) {
    logger.error('Failed to save customers to localStorage:', error);
    throw new Error('顧客データの保存に失敗しました');
  }
}

// 予約データの取得（ExtendedReservation型対応）
export function getReservations(): ExtendedReservation[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.RESERVATIONS);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    // 日付フィールドをDateオブジェクトに変換
    return parsed.map((reservation: Record<string, unknown>) => ({
      ...reservation,
      reservationDate: new Date(
        (reservation.reservationDate ?? reservation.date) as
          | string
          | number
          | Date
      ),
      customerRequest: (reservation.customerRequest ?? reservation.notes) as
        | string
        | undefined,
      internalNotes: (reservation.internalNotes ?? reservation.internalNote) as
        | string
        | undefined,
      createdAt: reservation.createdAt
        ? new Date(reservation.createdAt as string | number | Date)
        : undefined,
      updatedAt: reservation.updatedAt
        ? new Date(reservation.updatedAt as string | number | Date)
        : undefined,
    }));
  } catch (error) {
    logger.error('Failed to load reservations from localStorage:', error);
    return [];
  }
}

// 予約データの保存
export function saveReservations(reservations: ExtendedReservation[]): void {
  try {
    localStorage.setItem(
      STORAGE_KEYS.RESERVATIONS,
      JSON.stringify(reservations)
    );
  } catch (error) {
    logger.error('Failed to save reservations to localStorage:', error);
    throw new Error('予約データの保存に失敗しました');
  }
}

// 新規顧客の追加
export function addCustomer(
  customerData: Omit<
    Customer,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'customerCode'
    | 'messageSettings'
    | 'deliveryHistory'
    | 'mergeHistory'
    | 'mergeCandidates'
    | 'customFields'
  >
): Customer {
  const customers = getCustomers();

  // Generate UUID for new customer ID
  const newId = crypto.randomUUID();

  // シーケンス番号を取得してカスタマーコードを生成
  const sequences = getSequences();
  const customerSequence = (sequences.customer || 0) + 1;
  updateSequence('customer', customerSequence);

  const newCustomer: Customer = {
    ...customerData,
    id: newId,
    customerCode: generateCustomerCode(customerSequence),
    messageSettings: {
      email: true,
      myPage: true,
      push: false,
      sms: false,
    },
    deliveryHistory: [],
    mergeHistory: {
      mergedFrom: [],
    },
    mergeCandidates: [],
    customFields: {
      other1: '',
      other2: '',
      other3: '',
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  customers.push(newCustomer);
  saveCustomers(customers);
  return newCustomer;
}

// 顧客情報の更新
export function updateCustomer(
  id: string,
  updates: Partial<Customer>
): Customer {
  const customers = getCustomers();
  const index = customers.findIndex((c) => c.id === id);

  if (index === -1) {
    throw new Error('指定された顧客が見つかりません');
  }

  // medicalRecordsを除外したupdatesを作成
  const { medicalRecords: newMedicalRecords, ...otherUpdates } = updates;

  // 基本的な更新を適用
  const updatedCustomer = {
    ...customers[index],
    ...otherUpdates,
    updatedAt: new Date().toISOString(),
  };

  // medicalRecordsが更新に含まれている場合、既存の配列と連結する
  if (newMedicalRecords) {
    updatedCustomer.medicalRecords = [
      ...(customers[index].medicalRecords || []),
      ...newMedicalRecords,
    ];
  }

  customers[index] = updatedCustomer;

  saveCustomers(customers);
  return customers[index];
}

// 顧客の削除
export function deleteCustomer(id: string): void {
  const customers = getCustomers();
  const filteredCustomers = customers.filter((c) => c.id !== id);
  saveCustomers(filteredCustomers);
}

// 初期データの設定（モックデータから移行）
// NOTE: 施術記録関連の関数はBE API移行により削除済み (treatmentService.ts参照)

// 初期データの設定（モックデータから移行）
export function initializeWithMockData(
  mockCustomers: Partial<ExtendedCustomer>[]
): void {
  const existing = getCustomers();
  if (existing.length === 0) {
    const convertedCustomers: ExtendedCustomer[] = mockCustomers.map(
      (mock) =>
        ({
          id: mock.id ?? 0,
          firstName: mock.firstName || '',
          lastName: mock.lastName || '',
          nameKana: mock.nameKana || '',
          email: mock.email || '',
          phoneNumber: mock.phoneNumber || '',
          birthday: mock.birthday || '',
          gender: mock.gender || 'other',
          occupation: mock.occupation || '',
          address: mock.address || '',
          status: 'ACTIVE' as const,
          totalVisits:
            (mock as Partial<Customer> & { visitCount?: number }).visitCount ||
            0,
          ...mock,
          createdAt: new Date(
            Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000
          ).toISOString(),
          updatedAt: new Date().toISOString(),
        }) as ExtendedCustomer
    );
    saveCustomers(convertedCustomers);
  }
}

// データのクリア（開発用）
export function clearAllData(): void {
  localStorage.removeItem(STORAGE_KEYS.CUSTOMERS);
  localStorage.removeItem(STORAGE_KEYS.TREATMENTS);
  localStorage.removeItem(STORAGE_KEYS.RESERVATIONS);
}

// シーケンス番号の管理
export interface Sequences {
  customer?: number;
  reservation_BD?: number;
  reservation_YE?: number;
  reservation_WB?: number;
  reservation_OT?: number;
}

// シーケンス番号の取得
export function getSequences(): Sequences {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SEQUENCES);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    logger.error('Failed to load sequences from localStorage:', error);
    return {};
  }
}

// シーケンス番号の更新
export function updateSequence(key: keyof Sequences, value: number): void {
  const sequences = getSequences();
  sequences[key] = value;
  localStorage.setItem(STORAGE_KEYS.SEQUENCES, JSON.stringify(sequences));
}

// =================================
// スタッフシフト管理
// =================================

// 基本スタッフシフトデータの取得
export function getStaffShifts(): StaffShiftData[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.STAFF_SHIFTS);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    logger.error('Failed to load staff shifts from localStorage:', error);
    return [];
  }
}

// 基本スタッフシフトデータの保存
export function saveStaffShifts(shifts: StaffShiftData[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.STAFF_SHIFTS, JSON.stringify(shifts));
  } catch (error) {
    logger.error('Failed to save staff shifts to localStorage:', error);
    throw error;
  }
}

// 新しいスタッフシフトの追加
export function addStaffShift(
  shiftData: Omit<StaffShiftData, 'id' | 'createdAt' | 'updatedAt'>
): StaffShiftData {
  try {
    const shifts = getStaffShifts();
    const newShift: StaffShiftData = {
      id: `shift_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...shiftData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    shifts.push(newShift);
    saveStaffShifts(shifts);
    return newShift;
  } catch (error) {
    logger.error('Failed to add staff shift:', error);
    throw error;
  }
}

// スタッフシフトの更新
export function updateStaffShift(
  id: string,
  updates: Partial<StaffShiftData>
): StaffShiftData {
  try {
    const shifts = getStaffShifts();
    const index = shifts.findIndex((shift) => shift.id === id);

    if (index === -1) {
      throw new Error(`Staff shift with id ${id} not found`);
    }

    const updatedShift: StaffShiftData = {
      ...shifts[index],
      ...updates,
      id: shifts[index].id, // IDは変更不可
      createdAt: shifts[index].createdAt, // 作成日時は変更不可
      updatedAt: new Date().toISOString(),
    };

    shifts[index] = updatedShift;
    saveStaffShifts(shifts);
    return updatedShift;
  } catch (error) {
    logger.error('Failed to update staff shift:', error);
    throw error;
  }
}

// スタッフシフトの削除
export function deleteStaffShift(id: string): void {
  try {
    const shifts = getStaffShifts();
    const filteredShifts = shifts.filter((shift) => shift.id !== id);

    if (filteredShifts.length === shifts.length) {
      throw new Error(`Staff shift with id ${id} not found`);
    }

    saveStaffShifts(filteredShifts);
  } catch (error) {
    logger.error('Failed to delete staff shift:', error);
    throw error;
  }
}

// 拡張スタッフシフトデータの取得
export function getExtendedStaffShifts(): ExtendedStaffShiftData[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.STAFF_SHIFTS_EXTENDED);
    if (stored) {
      return JSON.parse(stored);
    }

    // 拡張データがない場合は基本データから変換
    const basicShifts = getStaffShifts();
    // 注意: 変換ロジックは実際の要件に応じて実装する必要があります
    return basicShifts.map((shift) => ({
      ...shift,
      shiftType: shift.workingHours ? 'work' : 'off',
      workDetails: shift.workingHours
        ? {
            presetType: 'custom',
            baseWorkingHours: shift.workingHours,
            shiftEvents: [],
          }
        : undefined,
    })) as ExtendedStaffShiftData[];
  } catch (error) {
    logger.error(
      'Failed to load extended staff shifts from localStorage:',
      error
    );
    return [];
  }
}

// 拡張スタッフシフトデータの保存
export function saveExtendedStaffShifts(
  shifts: ExtendedStaffShiftData[]
): void {
  try {
    localStorage.setItem(
      STORAGE_KEYS.STAFF_SHIFTS_EXTENDED,
      JSON.stringify(shifts)
    );

    // 後方互換性のため基本データも同期保存
    const basicShifts: StaffShiftData[] = shifts.map((shift) => ({
      id: shift.id,
      staffId: shift.staffId,
      date: shift.date,
      workingHours:
        shift.shiftType === 'work' && shift.workDetails?.baseWorkingHours
          ? shift.workDetails.baseWorkingHours
          : null,
      notes:
        ((shift as unknown as Record<string, unknown>).notes as string) || '',
      createdAt: shift.createdAt,
      updatedAt: shift.updatedAt,
    }));

    saveStaffShifts(basicShifts);
  } catch (error) {
    logger.error(
      'Failed to save extended staff shifts to localStorage:',
      error
    );
    throw error;
  }
}

export default {
  getCustomers,
  saveCustomers,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  getReservations,
  saveReservations,
  getSequences,
  updateSequence,
  initializeWithMockData,
  clearAllData,
  // スタッフシフト管理
  getStaffShifts,
  saveStaffShifts,
  addStaffShift,
  updateStaffShift,
  deleteStaffShift,
  getExtendedStaffShifts,
  saveExtendedStaffShifts,
};
